import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import {
  getPartner,
  getReports,
  getTierProgression,
  getPartnerQuarterlyRevenue,
  getProspects,
  getLeaderboard,
  getPartnerDocuments,
  uploadPartnerDocument,
  getResumeUrl,
} from '../api';
import type { Partner, ProductionReport, TierProgression, Prospect, QuarterlyRevenue, PartnerDocument } from '../types';
import PortalLayout from '../components/PortalLayout';
import CircularProgress from '../components/CircularProgress';
import PartnerRevenueChart from '../components/PartnerRevenueChart';
import ProspectPipeline from '../components/ProspectPipeline';
import PartnerActivityForm from '../components/PartnerActivityForm';

const CERTIFIED_THRESHOLD = 50000;
const GOLD = '#D4AF37';
const CHARCOAL = '#1A1A1A';

function GoldCheckmarkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

function downloadPerformancePDF(
  partner: Partner,
  reports: ProductionReport[],
  quarters: QuarterlyRevenue[],
  totalCollected: number
) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageW = doc.internal.pageSize.getWidth();
  let y = 20;

  doc.setFillColor(26, 26, 26);
  doc.rect(0, 0, pageW, 28, 'F');
  doc.setTextColor(212, 175, 55);
  doc.setFontSize(18);
  doc.text('The Station — Gold Label', 15, 18);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.text('Quarterly Performance Summary', 15, 24);

  y = 36;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(14);
  doc.text(partner.name, 15, y);
  y += 6;
  doc.setFontSize(10);
  doc.text(`${partner.tier} · ${partner.state_territory}`, 15, y);
  y += 10;

  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(0.5);
  doc.line(15, y, pageW - 15, y);
  y += 10;

  doc.setFontSize(11);
  doc.text(`Total collected revenue (all time): $${totalCollected.toLocaleString()}`, 15, y);
  y += 8;

  doc.text('Revenue by quarter:', 15, y);
  y += 6;
  quarters.forEach((q) => {
    doc.setFontSize(10);
    doc.text(`  ${q.quarter}: $${q.revenue.toLocaleString()}`, 15, y);
    y += 5;
  });
  y += 8;

  doc.text('Recent production reports:', 15, y);
  y += 6;
  reports.slice(0, 15).forEach((r) => {
    if (y > 270) { doc.addPage(); y = 20; }
    doc.setFontSize(9);
    doc.text(`  ${r.account_name} — $${r.collected_revenue.toLocaleString()} (${new Date(r.reported_at).toLocaleDateString()})`, 15, y);
    y += 5;
  });

  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  doc.text(`Generated ${new Date().toLocaleString()} · Station Gold Portal`, 15, doc.internal.pageSize.getHeight() - 10);

  doc.save(`Station-Gold-Performance-${partner.name.replace(/\s+/g, '-')}.pdf`);
}

export default function PartnerPortal() {
  const { partnerId } = useParams<{ partnerId: string }>();
  const [partner, setPartner] = useState<Partner | null>(null);
  const [reports, setReports] = useState<ProductionReport[]>([]);
  const [tierProgression, setTierProgression] = useState<TierProgression | null>(null);
  const [quarters, setQuarters] = useState<QuarterlyRevenue[]>([]);
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [leaderboard, setLeaderboard] = useState<{ fastest_territory_growth?: { id: number } | null } | null>(null);
  const [documents, setDocuments] = useState<PartnerDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [partnerId]);

  const refresh = () => {
    if (!partnerId) return;
    setLoading(true);
    Promise.all([
      getPartner(partnerId),
      getReports(partnerId),
      getTierProgression(partnerId),
      getPartnerQuarterlyRevenue(partnerId),
      getProspects(partnerId),
      getLeaderboard(),
      getPartnerDocuments(partnerId),
    ])
      .then(([p, r, t, q, pr, lb, docs]) => {
        setPartner(p);
        setReports(r.reports);
        setTierProgression(t);
        setQuarters(q.quarters);
        setProspects(pr.prospects);
        setLeaderboard(lb);
        setDocuments(docs.documents);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (partnerId) refresh();
  }, [partnerId]);

  if (loading) return <PortalLayout><div className="text-gold/80">Loading…</div></PortalLayout>;
  if (error) return <PortalLayout><div className="text-red-400">Error: {error}</div></PortalLayout>;
  if (!partner) return <PortalLayout><div className="text-station-muted">Partner not found.</div></PortalLayout>;

  const totalCollected = tierProgression?.total_collected_revenue ?? 0;
  const threshold = tierProgression?.threshold ?? CERTIFIED_THRESHOLD;
  const progressPct = partner.tier === 'Associate' ? Math.min(100, Math.round((totalCollected / threshold) * 100)) : 100;
  const awayFromCertified = Math.max(0, threshold - totalCollected);
  const isAtRisk = partner.territory_status === 'At Risk';
  const isFastestGrowth = leaderboard?.fastest_territory_growth?.id === partner.id;

  return (
    <PortalLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-semibold text-gold">{partner.name}</h1>
          <p className="text-station-muted mt-1">{partner.tier} · {partner.state_territory}</p>
        </div>

        {/* Julian: Territory At Risk banner */}
        {isAtRisk && (
          <section className="rounded-xl border-2 border-amber-500 bg-amber-500/20 p-5">
            <h2 className="text-lg font-semibold text-amber-400">Territory At Risk</h2>
            <p className="text-station-muted mt-1">No activity reported in the last 7 days. Submit activity below to clear this flag.</p>
          </section>
        )}

        {/* Personalized: Associate (Elena) vs Certified (Marcus) */}
        <section className="rounded-xl border border-leather bg-leather/40 p-5">
          {partner.tier === 'Associate' ? (
            <>
              <div className="flex items-center gap-4 mb-4">
                <CircularProgress progress={progressPct} size={56} strokeWidth={4} tier="Associate" />
                <div>
                  <h2 className="text-lg font-semibold text-gold">Advancement Path</h2>
                  <p className="text-station-muted text-sm">Progress to Certified Broker ($50,000)</p>
                </div>
              </div>
              {awayFromCertified > 0 && (
                <div className="mt-4 p-4 rounded-lg border border-gold/50 bg-gold/10">
                  <h3 className="text-sm font-semibold text-gold">Next Tier</h3>
                  <p className="text-white mt-1">You are ${awayFromCertified.toLocaleString()} away from Certified Broker (5%).</p>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-4">
                <GoldCheckmarkIcon className="w-12 h-12 text-gold shrink-0" />
                <div>
                  <h2 className="text-lg font-semibold text-gold">Certified Broker</h2>
                  <p className="text-station-muted text-sm">5% base commission on collected revenue</p>
                </div>
              </div>
              {isFastestGrowth && (
                <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold/20 border border-gold/50">
                  <span className="text-gold font-medium">+100% Growth</span>
                  <span className="text-station-muted text-xs">Fastest Territory Growth</span>
                </div>
              )}
            </>
          )}
        </section>

        {/* Submit Activity — highlighted for Julian */}
        <section className={isAtRisk ? 'rounded-xl border-2 border-gold bg-gold/10 p-5' : ''}>
          <h2 className="text-lg font-medium text-gold mb-4">
            Submit Activity {isAtRisk && <span className="text-amber-400">(clears At Risk)</span>}
          </h2>
          <PartnerActivityForm partnerId={partner.id} partnerTier={partner.tier} commissionRate={partner.commission_rate} onSuccess={refresh} />
        </section>

        {/* Submit Paperwork — PDF/Image upload to R2 */}
        <section className="rounded-xl border border-leather bg-leather/40 p-5">
          <h2 className="text-lg font-medium text-gold mb-4">Submit Paperwork</h2>
          <p className="text-station-muted text-sm mb-4">
            Upload PDF or image documents for admin review.
          </p>
          <div className="mb-4">
            <input
              type="file"
              accept=".pdf,image/*"
              className="block w-full text-sm text-station-muted file:mr-4 file:rounded file:border-0 file:bg-gold file:px-3 file:py-2 file:text-charcoal file:font-medium hover:file:bg-gold/90"
              disabled={uploading}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file || !partnerId) return;
                setUploading(true);
                try {
                  await uploadPartnerDocument(partnerId, file);
                  refresh();
                  e.target.value = '';
                } finally {
                  setUploading(false);
                }
              }}
            />
            {uploading && <p className="mt-1 text-xs text-gold/80">Uploading…</p>}
          </div>
          {documents.length > 0 && (
            <div className="rounded-lg border border-leather/50 overflow-hidden">
              <table className="w-full text-left text-sm">
                <thead className="bg-leather/50 text-gold/90">
                  <tr>
                    <th className="px-3 py-2 font-medium">File</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Submitted</th>
                    <th className="px-3 py-2 font-medium w-24" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-leather/50">
                  {documents.map((d) => (
                    <tr key={d.id}>
                      <td className="px-3 py-2 text-white">{d.file_name}</td>
                      <td className="px-3 py-2">
                        <span
                          className={
                            d.status === 'approved'
                              ? 'text-green-400'
                              : d.status === 'rejected'
                                ? 'text-red-400'
                                : 'text-amber-400'
                          }
                        >
                          {d.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-station-muted">{new Date(d.created_at).toLocaleDateString()}</td>
                      <td className="px-3 py-2">
                        <a
                          href={getResumeUrl(d.r2_key)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gold hover:underline"
                        >
                          View
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* My Performance: chart + PDF */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gold">My Performance</h2>
            <button
              type="button"
              onClick={() => downloadPerformancePDF(partner, reports, quarters, totalCollected)}
              className="px-4 py-2 rounded-lg bg-gold text-charcoal font-medium hover:bg-gold-light transition-colors"
            >
              Download Performance PDF
            </button>
          </div>
          <PartnerRevenueChart data={quarters} />
        </section>

        {/* Prospect Pipeline */}
        <ProspectPipeline partnerId={partner.id} prospects={prospects} onRefresh={refresh} />
      </div>
    </PortalLayout>
  );
}
